<template>
	<div :key="componentInstanceId">
		<div v-if="compact" :key="`${componentInstanceId}-${displayedHypermarket}`" class="max-md:mb-4">
			<slot
				v-if="$slots.pageHeading"
				name="pageHeading"
			/>
			<div v-if="!disabledInformationAbout" class="typo-h1 whitespace-nowrap inline-flex mr-2 lg:mr-4 mb-2">
				{{ displayedHypermarket }}
			</div>
			<button
				v-if="!disabledInformationAbout && !hideChangeButton"
				class="btn btn-secondary btn-sm whitespace-nowrap inline-flex mr-2 lg:mr-4 align-top mt-0.5"
				@click="isSelectorVisible = true"
			>
				<NuxtIcon
					name="chevron-right"
					class="text-icon-primary self-center ml-auto group-hover/interactive:text-icon-neutral transition-colors"
					filled
				/>
				Změnit pobočku
			</button>
		</div>
		<div
			v-else-if="!disabledInformationAbout"
			:key="`full-${displayedHypermarket}-${showDropdown ? 'dropdown' : 'button'}`"
			class="flex max-md:flex-wrap items-start md:items-center gap-1 md:gap-2 text-body-quaternary md:bg-background-quaternary md:px-4 md:py-3 rounded-xl"
		>
			<div class="flex items-center gap-1" :class="smallText ? 'typo-body-s' : 'md:gap-2.5'">
				<NuxtIcon
					name="info"
					filled
					class="mt-1 md:mt-0.5 lg:mt-0 text-xs text-icon-neutral"
					:class="smallText ? 'text-sm' : 'sm:text-sm md:text-xl'"
				/>
				<div>
					<span v-if="text" v-html="text" /> <strong v-if="!showDropdown" class="text-body-secondary whitespace-nowrap mr-1.5">{{ displayedHypermarket }}.</strong>

					<button
						v-if="!hideChangeButton"
						class="text-body-link underline underline-offset-1 hover:no-underline md:hidden whitespace-nowrap"
						:class="[showDropdown && 'max-md:hidden', smallText && 'typo-body-s']"
						@click="isSelectorVisible = true"
					>
						Změnit pobočku
					</button>
				</div>
			</div>
			<FormKit
				v-if="showDropdown"
				v-model="selectedHypermarketWithFallback"
				type="selectbox"
				:options="options"
				placeholder="Vybrat pobočku"
				:classes="{
					outer: 'max-md:w-full md:max-w-[32.5rem] min-w-56 text-body-primary',
					inner: 'border border-border-tertiary !rounded-30xl max-w-none',
					input: 'py-[0.375rem] !bg-transparent pl-4 pr-1.5 typo-body-caption',
					dropdown: 'min-w-72'
				}"
				search="true"
				dropdown-end="true"
			>
				<template #selectIcon="context">
					<NuxtIcon
						name="chevron-right"
						class="text-icon-primary text-2xl transition duration-300"
						:class="{'rotate-90': !context.state.open}"
						filled
					/>
				</template>
			</FormKit>
			<button
				v-else-if="!hideChangeButton"
				class="btn btn-secondary btn-sm max-md:hidden whitespace-nowrap"
				:class="[smallText && 'btn-xs']"
				@click="isSelectorVisible = true"
			>
				<NuxtIcon
					name="chevron-right"
					class="text-icon-primary self-center ml-auto group-hover/interactive:text-icon-neutral transition-colors"
					filled
				/>
				Změnit pobočku
			</button>
		</div>
		<PartialsHypermarketSelectionPopup
			v-model:visible="isSelectorVisible"
		/>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '@/stores/storyblokHypermarkets';
	import {DEFAULT_HYPERMARKET} from '#shared/constants';
	import type {Option} from '~/types/formkitSelectbox';

	type Props = {
		text?: string;
		showDropdown?: boolean;
		defaultHm?: string;
		compact?: boolean;
		showLinksInSelectionBanner?: boolean;
		hideChangeButton?: boolean;
		smallText?: boolean;
	};

	const props = defineProps<Props>();

	const route = useRoute();
	const disabledInformationAboutBySlugs = ['blog-category', 'recepty-category'];
	const disabledInformationAbout = computed(() => disabledInformationAboutBySlugs.includes(route.name as string));

	const componentInstanceId = useId();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance, branches, groupedBranches} = storeToRefs(hypermarketsStore);
	const isSelectorVisible = ref(false);
	const selectedHypermarketWithFallback = computed({
		get() {
			return selectedHypermarket.value ?? DEFAULT_HYPERMARKET;
		},
		set(newValue) {
			hypermarketsStore.setHypermarket(newValue);
		},
	});

	function createSectionHeader(section: Pick<Option, 'label' | 'value' | 'icon' | 'info'>): Option {
		return {
			...section,
			disabled: true,
			sectionHeader: true,
		};
	}

	const options = computed<Option[]>(() => {
		const hypermarketOptions = groupedBranches.value.hypermarkets
			.map((branch) => ({
				label: branch.content.name,
				value: branch.slug,
			}));
		const freshOptions = groupedBranches.value.fresh
			.map((branch) => ({
				label: branch.content.name,
				value: branch.slug,
			}));

		return [
			...(hypermarketOptions.length
				? [
					createSectionHeader({
						label: 'Hypermarkety',
						value: '__section-hypermarkets',
						icon: 'custom/company',
					}),
					...hypermarketOptions,
				]
				: []),
			...(freshOptions.length
				? [
					createSectionHeader({
						label: 'Prodejny Fresh',
						value: '__section-fresh-stores',
						icon: 'custom/house',
						info: {
							title: 'Co je fresh?',
							tooltip: 'Fresh je malý formát našich prodejen, kde naleznete specifické zboží pro danou lokalitu.',
						},
					}),
					...freshOptions,
				]
				: []),
		];
	});

	const displayedHypermarket = computed(() => selectedHypermarketInstance.value?.content?.name
		|| branches.value?.find((hm) => hm.slug === props.defaultHm)?.content?.name
		|| 'Globus');
</script>
